import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { requestNotificationPermission, onForegroundMessage } from '../lib/firebase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Axios instance
export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Format API error
const formatApiError = (detail) => {
  if (detail == null) return 'Si è verificato un errore. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Auto-refresh interceptor: if 401, try refresh token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = sessionStorage.getItem('refresh_token');
      if (!refreshToken) return Promise.reject(error);

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: refreshToken });
        const newToken = data.access_token;
        sessionStorage.setItem('access_token', newToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        processQueue(null, newToken);
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshTimerRef = useRef(null);

  const scheduleTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    // Refresh 30 minutes before expiry (token lasts 12h, refresh at 11.5h)
    refreshTimerRef.current = setTimeout(async () => {
      const refreshToken = sessionStorage.getItem('refresh_token');
      if (!refreshToken) return;
      try {
        const { data } = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: refreshToken });
        if (data.access_token) {
          sessionStorage.setItem('access_token', data.access_token);
          api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
          console.log('Token auto-refreshed');
          scheduleTokenRefresh();
        }
      } catch (e) {
        console.log('Auto-refresh failed');
      }
    }, 11.5 * 60 * 60 * 1000); // 11h 30min
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        setUser(false);
        setLoading(false);
        return;
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const { data } = await api.get('/auth/me');
      setUser(data);
      scheduleTokenRefresh();
    } catch (e) {
      // Try refresh
      const refreshToken = sessionStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: refreshToken });
          if (data.access_token) {
            sessionStorage.setItem('access_token', data.access_token);
            api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
            const { data: userData } = await api.get('/auth/me');
            setUser(userData);
            scheduleTokenRefresh();
            return;
          }
        } catch (refreshErr) {
          // Refresh also failed
        }
      }
      setUser(false);
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  }, [scheduleTokenRefresh]);

  useEffect(() => {
    checkAuth();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [checkAuth]);

  const registerPushToken = useCallback(async () => {
    try {
      const subscription = await requestNotificationPermission();
      if (subscription) {
        await api.post('/push/register', { subscription });
        console.log('Push subscription registered');
      }
    } catch (e) {
      console.log('Push registration skipped:', e.message);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      if (Notification.permission === 'granted' && payload.title) {
        new Notification(payload.title, {
          body: payload.body || '',
          icon: '/icon-192.png'
        });
      }
    });
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, []);

  // ==================== HEARTBEAT (Presence Tracking) ====================
  // Ogni 60s ping al backend per segnalare che l'utente e' online.
  // Pinga subito al login + ogni 60s + quando la tab torna visibile.
  useEffect(() => {
    if (!user || user === false) return;
    let stopped = false;

    const ping = async () => {
      if (stopped) return;
      try {
        await api.post('/presence/ping');
      } catch (e) {
        // silenzioso: il ping puo' fallire se il token scade, l'interceptor gestisce il refresh
      }
    };

    ping(); // ping immediato al mount/login
    const intervalId = setInterval(ping, 60_000); // ogni 60s

    const onVisibility = () => {
      if (document.visibilityState === 'visible') ping();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stopped = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user]);

  const login = async (email, password) => {
    setError(null);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.access_token) {
        sessionStorage.setItem('access_token', data.access_token);
        api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      }
      if (data.refresh_token) {
        sessionStorage.setItem('refresh_token', data.refresh_token);
      }
      setUser(data);
      scheduleTokenRefresh();
      registerPushToken();
      return data;
    } catch (e) {
      const errorMsg = formatApiError(e.response?.data?.detail) || e.message;
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      console.log('Logout request failed, clearing local state');
    } finally {
      setUser(false);
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      delete api.defaults.headers.common['Authorization'];
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'super_admin',
    isHotelManager: user?.role === 'hotel_manager',
    isTechnician: user?.role === 'technician',
    isHousekeeping: user?.role === 'housekeeping',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
